import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Marker, InfoWindow } from '@react-google-maps/api';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { useGoogleMap } from '/imports/gmaps';
import { ProspectorMap } from '/imports/ui/ProspectorMap';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { ProjectManagementPipelineRow, projectManagementGeocodeMissing } from '/imports/api/projectManagement';

const hasCoords = (r: ProjectManagementPipelineRow) => typeof r.project.lat === 'number' && typeof r.project.lng === 'number';

const MapStyled = styled.div`
  height: 100%;
  position: relative;
  .banner {
    position: absolute; top: 0.5rem; left: 50%; transform: translateX(-50%); z-index: 3;
    background: ${css.white}; border: ${css.inputBorder}; border-radius: 1rem;
    padding: 0.25rem 0.75rem; font-size: 0.8rem; color: ${css.grey3}; box-shadow: 0 1px 4px rgba(0,0,0,0.15);
  }
  .iw-name { font-weight: 600; }
  .iw-link { color: ${css.primaryColor}; cursor: pointer; }
`;

interface Props {
  rows: ProjectManagementPipelineRow[];
  lifecycleKey: string;
  reload?: () => void;  // only needed by method-loaded callers; reactive callers see geocodes stream in
}

/** Map view: plots geocoded project sites; backfills missing coords on-demand via the gmaps geocode. */
export const ProjectManagementMapView = ({ rows, lifecycleKey, reload }: Props) => {
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMap();
  const [selectedId, setSelectedId] = useState<string>();
  const attempted = useRef('');

  const pinned = rows.filter(hasCoords);
  const missing = rows.filter((r) => r.project.address && !hasCoords(r));

  // Backfill any projects with an address but no coords (once per lifecycle), then refresh.
  useEffect(() => {
    if (!missing.length || attempted.current === lifecycleKey) return;
    attempted.current = lifecycleKey;
    projectManagementGeocodeMissing.call({ lifecycleKey })
      .then((res) => { if (res?.geocoded) reload?.(); })
      .catch(() => undefined);
  }, [lifecycleKey, missing.length]);

  const onMapLoad = (map: google.maps.Map) => {
    if (!pinned.length) return;
    const bounds = new google.maps.LatLngBounds();
    pinned.forEach((r) => bounds.extend({ lat: r.project.lat as number, lng: r.project.lng as number }));
    map.fitBounds(bounds);
    if (pinned.length === 1) map.setZoom(12);
  };

  if (!isLoaded) return <LoadingFlex />;
  const selected = pinned.find((r) => r.project._id === selectedId);

  return (
    <MapStyled>
      {missing.length > 0 && <div className="banner">{missing.length} {strings.Geocoding()}…</div>}
      <ProspectorMap zoom={5} mapContainerStyle={{ width: '100%', height: '100%' }} onMapLoad={onMapLoad}>
        {
          pinned.map((r) => (
            <Marker
              key={r.project._id}
              position={{ lat: r.project.lat as number, lng: r.project.lng as number }}
              onClick={() => setSelectedId(r.project._id)}
            />
          ))
        }
        {
          selected &&
            <InfoWindow
              position={{ lat: selected.project.lat as number, lng: selected.project.lng as number }}
              onCloseClick={() => setSelectedId(undefined)}
            >
              <div>
                <div className="iw-name">{selected.project.name}</div>
                {selected.project.address && <div>{selected.project.address}</div>}
                <div className="iw-link" onClick={() => navigate(`/project-management/projects/${selected.project._id}`)}>{strings.View()}</div>
              </div>
            </InfoWindow>
        }
      </ProspectorMap>
    </MapStyled>
  );
};
